import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { Box } from '@mui/material';
import { CoContractorIcon, StarIcon } from '@synergy/theme';
import { ReactNode } from 'react';
import { UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { hasRole, padZeroLeft } from '@synergy/utils';
import urljoin from 'url-join';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { PoolAutocompleteListAction } from '../pool-autocomplete-list-action';
import { PoolAutocompleteListFooter } from '../pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';
import { ROUTES } from '../../../../features/sidebar-menu/navigation-drawer/routes';
import { Supplier } from '../../../models/supplier';
import { SupplierApi } from '../../../api/supplier-api';
import { User } from '../../../models/user';

export interface PartialSupplier extends BaseAutocompleteSuggestionType {
    icons: ReactNode;
    name: string;
    city: string;
}

interface PoolSupplierAutocompleteListProps {
    user: User;
    inProgress: boolean;

    /**
     * Filter suppliers by specifying a customer for the blacklist/whitelist filter.
     */
    useSupplierFilterForCustomerId?: number;

    isLocked?: boolean;
}

class PoolSupplierAutocompleteListBase extends PoolFormAutocompleteList<
    Supplier,
    PartialSupplier,
    PoolSupplierAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];
    private api = new SupplierApi(API_BASE_URL);
    private apiAbortController?: AbortController;

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.autocomplete.footer.suppliers"
            url={urljoin(BASE_URL, ROUTES.contactsAndOrganizations.suppliers.new)}
        />
    );

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public getSuggestionValue = (suggestion: PartialSupplier) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        const searchParams = new URLSearchParams({ search, limit: '40' });
        if (this.props.useSupplierFilterForCustomerId) {
            searchParams.set('useSupplierFilterForCustomerId', this.props.useSupplierFilterForCustomerId?.toString());
        }

        if (this.props.isLocked !== undefined && this.props.isLocked !== null) {
            searchParams.set('isLocked', `${this.props.isLocked}`);
        }

        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.cget(searchParams, this.apiAbortController.signal);
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success === true ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (suppliers: Supplier[]): PartialSupplier[] =>
        suppliers.map(supplier => {
            const editLink = urljoin(
                BASE_URL,
                ROUTES.contactsAndOrganizations.suppliers.detail.replace(':id', supplier.id.toString())
            );
            return {
                id: padZeroLeft(supplier.id, 5),
                icons: (
                    <Box sx={{ display: 'inline-flex', verticalAlign: 'middle', gap: 1 }}>
                        {supplier.isMain && <StarIcon sx={{ fontSize: 20 }} />}
                        {supplier.isContractPartner && <CoContractorIcon sx={{ fontSize: 20 }} />}
                    </Box>
                ),
                name: supplier.name,
                city: supplier.getCity() || '',
                tags: supplier.getTags(30),
                actions: <PoolAutocompleteListAction url={editLink} allowedRoles={this.actionRoles} />,
            };
        });

    public getDisplayValue(supplier: Supplier) {
        const city = (
            supplier.mainAddress ||
            supplier.addresses?.find(address => address.isMainAddress) ||
            supplier.addresses?.[0]
        )?.city;
        return city ? `${supplier.name}, ${city}` : supplier.name;
    }
}

const mapStateToProps = (state: AppState) => ({
    inProgress: state.suppliers.requesting,
    user: state.user,
});

export const PoolSupplierAutocompleteList = connect(mapStateToProps, {})(PoolSupplierAutocompleteListBase);
